import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

function ColourPicker({ label, value, onChange, hint }) {
  const [hex, setHex] = useState(value || '')

  const handleHexChange = (e) => {
    const val = e.target.value.replace('#', '').toUpperCase()
    setHex(val)
    if (val.length === 6) onChange(val)
  }

  const presets = [
    '1A4035', '2D6A4F', '3D8B6A', 'C9A84C',
    '1C1C1C', '2B6CB0', 'C0392B', '7B2D8B',
    '1A365D', 'E67E22', '27AE60', '8B0000'
  ]

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        fontSize: '0.75rem', fontWeight: 600,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        color: 'var(--green)', marginBottom: '8px'
      }}>
        {label}
      </div>
      {hint && (
        <div style={{
          fontSize: '0.75rem', color: 'var(--slate)',
          fontStyle: 'italic', marginBottom: '8px'
        }}>
          {hint}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        {/* Colour preview */}
        <div style={{
          width: '48px', height: '48px',
          background: `#${hex || 'CCCCCC'}`,
          border: '1px solid var(--silver)',
          flexShrink: 0
        }} />
        {/* Native colour picker */}
        <input
          type="color"
          value={`#${hex || '000000'}`}
          onChange={e => {
            const val = e.target.value.replace('#', '').toUpperCase()
            setHex(val)
            onChange(val)
          }}
          style={{
            width: '48px', height: '48px',
            border: '1px solid var(--silver)',
            cursor: 'pointer', padding: '2px'
          }}
        />
        {/* Hex input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.9375rem', color: 'var(--slate)', fontWeight: 600 }}>#</span>
          <input
            type="text"
            value={hex}
            onChange={handleHexChange}
            maxLength={6}
            placeholder="2D6A4F"
            style={{
              width: '96px', padding: '8px 10px',
              border: '1px solid var(--silver)',
              fontSize: '0.9375rem', fontFamily: 'monospace',
              borderRadius: 0, outline: 'none',
              letterSpacing: '0.1em'
            }}
          />
        </div>
      </div>
      {/* Preset swatches */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {presets.map(preset => (
          <button
            key={preset}
            onClick={() => { setHex(preset); onChange(preset) }}
            style={{
              width: '28px', height: '28px',
              background: `#${preset}`,
              border: hex === preset ? '2px solid var(--charcoal)' : '1px solid transparent',
              cursor: 'pointer',
              outline: 'none'
            }}
            title={`#${preset}`}
          />
        ))}
      </div>
    </div>
  )
}

function BrandingSettings({ session, profile, onNavigate, onBrandingUpdated, onSettingsSaved, targetOrgId }) {
  const [, setOrg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)

  const [form, setForm] = useState({
    name: '',
    primary_colour: '2D6A4F',
    secondary_colour: 'C9A84C',
    logo_url: '',
    powered_by_cadence: true,
    ai_enabled: false,
    button_colour: '2D5A3D',
    header_colour: '1A3A2A',
    accent_colour: '2D5A3D'
  })

  const isAdmin = profile?.role === 'super_admin' || profile?.role === 'admin'

  useEffect(() => {
    loadOrg()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadOrg = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('organisations')
      .select('*')
      .eq('id', targetOrgId || profile.organisation_id)
      .single()
    if (data) {
      setOrg(data)
      setForm({
        name: data.name || '',
        primary_colour: data.primary_colour || '2D6A4F',
        secondary_colour: data.secondary_colour || 'C9A84C',
        logo_url: data.logo_url || '',
        powered_by_cadence: data.powered_by_cadence !== false,
      ai_enabled: data.ai_enabled || false
      })
      if (data.logo_url) setLogoPreview(data.logo_url)
    }
    setLoading(false)
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
   console.log('File type:', file.type, 'File size:', file.size, 'File name:', file.name)
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp']
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PNG, JPG, SVG or WebP file.')
      return
    }

    // Validate file size — max 2MB
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be smaller than 2MB.')
      return
    }

    setUploading(true)
    setError(null)

    const ext = file.name.split('.').pop()
    const fileName = `${profile.organisation_id}/logo_${Date.now()}.${ext}`

    console.log('Uploading to:', fileName, 'org:', profile.organisation_id)
    const { error: uploadError } = await supabase.storage
      .from('logos')
      .upload(fileName, file, { upsert: true })
    console.log('Upload error:', uploadError)

    if (uploadError) {
      setError(uploadError.message)
      setUploading(false)
      return
    }

    const { data: urlData } = supabase.storage
      .from('logos')
      .getPublicUrl(fileName)

    const logoUrl = urlData.publicUrl
    console.log('Logo URL generated:', logoUrl)
    setForm(prev => ({ ...prev, logo_url: logoUrl }))
    setLogoPreview(logoUrl)
    setUploading(false)
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)

    const payload = {
        name: form.name.trim(),
        primary_colour: form.primary_colour,
        secondary_colour: form.secondary_colour,
        logo_url: form.logo_url || null,
        powered_by_cadence: form.powered_by_cadence,
        ai_enabled: form.ai_enabled,
        button_colour: form.button_colour || "2D5A3D",
        header_colour: form.header_colour || "1A3A2A",
        accent_colour: form.accent_colour || "2D5A3D"
      }
      console.log("Saving branding:", payload)
      const { error } = await supabase
      .from('organisations')
      .update({
        name: form.name.trim(),
        primary_colour: form.primary_colour,
        secondary_colour: form.secondary_colour,
        logo_url: form.logo_url || null,
        powered_by_cadence: form.powered_by_cadence,
        ai_enabled: form.ai_enabled,
        button_colour: form.button_colour || "2D5A3D",
        header_colour: form.header_colour || "1A3A2A",
        accent_colour: form.accent_colour || "2D5A3D"
      })
      .eq('id', targetOrgId || profile.organisation_id)

    setSaving(false)

    if (error) {
      setError(error.message)
      return
    }

    setSuccess('Branding saved successfully. All future exports will use your updated brand settings.')
    if (onBrandingUpdated) onBrandingUpdated()
    if (onSettingsSaved) onSettingsSaved()
  }

  const handleRemoveLogo = async () => {
    setForm(prev => ({ ...prev, logo_url: '' }))
    setLogoPreview(null)
  }

  if (loading) {
    return (
      <div className="scorecard-page container">
        <p style={{ color: 'var(--slate)' }}>Loading branding settings...</p>
      </div>
    )
  }

  return (
    <div className="scorecard-page container">
      <div className="page-header">
        <p className="label">Organisation Settings</p>
        <h1>Branding</h1>
        <p>
          Configure your organisation's brand colours and logo. These will be applied
          to all PowerPoint exports produced by Cadence for your organisation.
        </p>
      </div>

      {!isAdmin && (
        <div style={{
          background: '#FFF8E7', borderLeft: '3px solid var(--gold)',
          padding: '16px 20px', marginBottom: '24px',
          fontSize: '0.875rem', color: 'var(--gold-dark)'
        }}>
          You need Admin access to edit branding settings. Contact your organisation administrator.
        </div>
      )}

      {error && <div className="error-msg">{error}</div>}
      {success && <div className="success-msg">{success}</div>}

      {/* Preview strip */}
      <div style={{
        background: `#${form.primary_colour}`,
        padding: '20px 24px', marginBottom: '24px',
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {logoPreview ? (
            <img
              src={logoPreview}
              alt="Organisation logo"
              style={{ height: '40px', objectFit: 'contain' }}
            />
          ) : (
            <div style={{
              width: '40px', height: '40px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.625rem', color: 'rgba(255,255,255,0.6)',
              fontWeight: 600, letterSpacing: '0.06em', textAlign: 'center'
            }}>
              LOGO
            </div>
          )}
          <div>
            <div style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: '1.25rem', fontWeight: 600, color: 'white'
            }}>
              {form.name || 'Your Organisation'}
            </div>
            {form.powered_by_cadence && (
              <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.1em' }}>
                POWERED BY CADENCE
              </div>
            )}
          </div>
        </div>
        <div style={{
          background: `#${form.secondary_colour}`,
          padding: '6px 16px',
          fontSize: '0.75rem', fontWeight: 700,
          color: 'white', letterSpacing: '0.06em'
        }}>
          SAMPLE BADGE
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <span className="card__title">Organisation Name</span>
        </div>
        <div className="form-group">
          <label>Display Name</label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Your organisation name"
            disabled={!isAdmin}
          />
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <span className="card__title">Brand Colours</span>
        </div>
        <ColourPicker
          label="Primary Colour"
          value={form.primary_colour}
          onChange={val => setForm({ ...form, primary_colour: val })}
          hint="Used for slide headers, section dividers, and primary backgrounds."
        />
        <ColourPicker
          label="Secondary Colour"
          value={form.secondary_colour}
          onChange={val => setForm({ ...form, secondary_colour: val })}
          hint="Used for accent bars, highlights, and badge colours."
        />
      </div>

      <div className="card">
        <div className="card__header">
          <span className="card__title">Logo</span>
        </div>
        <div style={{ marginBottom: '16px', fontSize: '0.875rem', color: 'var(--slate)' }}>
          Upload your organisation logo. PNG or SVG recommended. Maximum 2MB.
          The logo will appear in the top right of every exported slide.
        </div>

        {logoPreview && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center',
              gap: '16px', padding: '12px 16px',
              background: 'var(--green-pale)',
              border: '1px solid var(--green-light)'
            }}>
              <img
                src={logoPreview}
                alt="Logo preview"
                style={{ height: '48px', objectFit: 'contain', maxWidth: '200px' }}
              />
              {isAdmin && (
                <button
                  onClick={handleRemoveLogo}
                  style={{
                    background: 'none', border: '1px solid var(--silver)',
                    color: 'var(--slate)', padding: '4px 10px',
                    fontSize: '0.75rem', cursor: 'pointer'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        )}

        {isAdmin && (
          <div>
            <label
              htmlFor="logo-upload"
              style={{
                display: 'inline-block',
                background: 'none', border: '1px solid var(--green)',
                color: 'var(--green)', padding: '8px 20px',
                fontSize: '0.875rem', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--green)'; e.currentTarget.style.color = 'var(--white)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--green)' }}
            >
              {uploading ? 'Uploading...' : logoPreview ? 'Replace Logo' : 'Upload Logo'}
            </label>
            <input
              id="logo-upload"
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={(e) => {
                console.log('File selected:', e.target.files[0])
                handleLogoUpload(e)
              }}
              style={{ display: 'none' }}
              disabled={uploading}
            />
          </div>
        )}
      </div>

      <div className="card">
        <div className="card__header">
          <span className="card__title">Extended Brand Colours</span>
        </div>
        {isAdmin ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Button Colour</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input type="color" value={'#' + form.button_colour} onChange={e => setForm({ ...form, button_colour: e.target.value.replace('#', '') })} style={{ width: '48px', height: '36px', border: '1px solid var(--silver)', cursor: 'pointer', padding: '2px' }} />
                <input type="text" value={form.button_colour} onChange={e => setForm({ ...form, button_colour: e.target.value.replace('#', '') })} maxLength={6} style={{ flex: 1, fontFamily: 'monospace', textTransform: 'uppercase' }} placeholder="2D5A3D" />
              </div>
              <p style={{ fontSize: '0.6875rem', color: 'var(--slate)', margin: '4px 0 0' }}>Primary action buttons</p>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Header & Title Colour</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input type="color" value={'#' + form.header_colour} onChange={e => setForm({ ...form, header_colour: e.target.value.replace('#', '') })} style={{ width: '48px', height: '36px', border: '1px solid var(--silver)', cursor: 'pointer', padding: '2px' }} />
                <input type="text" value={form.header_colour} onChange={e => setForm({ ...form, header_colour: e.target.value.replace('#', '') })} maxLength={6} style={{ flex: 1, fontFamily: 'monospace', textTransform: 'uppercase' }} placeholder="1A3A2A" />
              </div>
              <p style={{ fontSize: '0.6875rem', color: 'var(--slate)', margin: '4px 0 0' }}>Card titles and section headers</p>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Link & Accent Colour</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input type="color" value={'#' + form.accent_colour} onChange={e => setForm({ ...form, accent_colour: e.target.value.replace('#', '') })} style={{ width: '48px', height: '36px', border: '1px solid var(--silver)', cursor: 'pointer', padding: '2px' }} />
                <input type="text" value={form.accent_colour} onChange={e => setForm({ ...form, accent_colour: e.target.value.replace('#', '') })} maxLength={6} style={{ flex: 1, fontFamily: 'monospace', textTransform: 'uppercase' }} placeholder="2D5A3D" />
              </div>
              <p style={{ fontSize: '0.6875rem', color: 'var(--slate)', margin: '4px 0 0' }}>Links, active states and highlights</p>
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--slate)', fontSize: '0.875rem' }}>Only Admins can edit brand colours.</p>
        )}
      </div>
      <div className="card">
        <div className="card__header">
          <span className="card__title">Cadence Credit</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <input
            type="checkbox"
            id="powered-by"
            checked={form.powered_by_cadence}
            onChange={e => setForm({ ...form, powered_by_cadence: e.target.checked })}
            disabled={!isAdmin}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <label htmlFor="powered-by" style={{ cursor: 'pointer' }}>
            <div style={{ fontWeight: 600, color: 'var(--charcoal)', marginBottom: '2px' }}>
              Show "Powered by Cadence" on exports
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--slate)' }}>
              When enabled a small Cadence credit appears on the title slide of all exports.
              Enterprise clients may wish to disable this for fully white-labelled outputs.
            </div>
          </label>
        </div>
      </div>

      {isAdmin && (
        <div className="actions">
          <button
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Branding'}
          </button>
          <button
            className="btn btn--secondary"
            onClick={() => onNavigate('dashboard')}
          >
            Cancel
          </button>
        </div>
      )}

      {!isAdmin && (
        <div className="actions">
          <button
            className="btn btn--secondary"
            onClick={() => onNavigate('dashboard')}
          >
            ← Dashboard
          </button>
        </div>
      )}
    </div>
  )
}

export default BrandingSettings